/**
 * Telegram `startapp` deep links (SCREENS → Navigation): the share link and where each link
 * lands in the app. Parsing lives in @spot/shared (the bot uses it too).
 */
import { miniAppLink, parseStartParam, type DeepLink } from '@spot/shared';

import {
	TAB_PATHS,
	eventApplicationsPath,
	eventChatPath,
	eventPath,
	eventReviewPath,
} from '../app/paths';

export { parseStartParam } from '@spot/shared';
export type { DeepLink } from '@spot/shared';

/** Share link that opens the event in the Mini App: t.me/<bot>/<app>?startapp=event_<id>. */
export function eventShareLink(bot: string, app: string, eventId: string): string {
	return miniAppLink(bot, app, { kind: 'event', eventId });
}

/**
 * Where a deep link lands. Reviews open the event screen with the sheet on top (it says when the
 * window is closed); profiles fall back to the map.
 */
export function deepLinkPath(link: DeepLink): string {
	switch (link.kind) {
		case 'chat':
			return eventChatPath(link.eventId);
		case 'applications':
			return eventApplicationsPath(link.eventId);
		case 'event':
			return eventPath(link.eventId);
		case 'review':
			return eventReviewPath(link.eventId);
		case 'user':
			return TAB_PATHS.map;
	}
}

/**
 * On a fresh launch (app opened at `/`), puts the map under the linked screen so Telegram's
 * BackButton returns to the map. Reloads keep the current URL. Call before the router mounts.
 */
export function applyStartParam(
	startParam: string | null,
	history: Pick<History, 'replaceState' | 'pushState'>,
	pathname: string,
): void {
	const link = parseStartParam(startParam);
	if (link === null || pathname !== '/') return;
	const target = deepLinkPath(link);
	history.replaceState(null, '', TAB_PATHS.map);
	if (target !== TAB_PATHS.map) history.pushState(null, '', target);
}
